import React, { useRef, useEffect, useState } from 'react';
import { Stage, Layer, Image, Text, Transformer, Group, Line, Rect, Circle, Arrow } from 'react-konva';
import { TemplateElement } from '../utils/templateService';

interface KonvaCanvasProps {
  elements: TemplateElement[];
  onElementsChange: (elements: TemplateElement[]) => void;
  selectedElementId: string | null;
  onElementSelect: (elementId: string | null) => void;
  canvasScale: number;
  width: number;
  height: number;
  onStageRef?: (stage: any) => void;
  hideGrid?: boolean;
  onTextEdit?: (elementId: string, currentText: string) => void;
}

const KonvaCanvas: React.FC<KonvaCanvasProps> = ({
  elements,
  onElementsChange,
  selectedElementId,
  onElementSelect,
  canvasScale,
  width,
  height,
  onStageRef,
  hideGrid = false,
  onTextEdit,
}) => {
  const stageRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);
  const [images, setImages] = useState<{ [key: string]: HTMLImageElement }>({});

  // Load images for image elements
  useEffect(() => {
    const loadImages = async () => {
      const imageElements = elements.filter(el => el.type === 'image' && el.src);
      const newImages: { [key: string]: HTMLImageElement } = {};

      for (const element of imageElements) {
        if (element.src && !images[element.id]) {
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          img.src = element.src;
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });
          newImages[element.id] = img;
        }
      }

      setImages(prev => ({ ...prev, ...newImages }));
    };

    loadImages();
  }, [elements]);

  // Update transformer when selection changes
  useEffect(() => {
    if (selectedElementId && transformerRef.current) {
      const stage = stageRef.current;
      const selectedNode = stage.findOne(`#${selectedElementId}`);
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
        transformerRef.current.getLayer().batchDraw();
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedElementId]);

  // Pass stage ref to parent
  useEffect(() => {
    if (onStageRef && stageRef.current) {
      onStageRef(stageRef.current);
    }
  }, [onStageRef]);

  const handleElementClick = (elementId: string) => {
    const el = elements.find(e => e.id === elementId);
    if (el?.locked) return; // Prevent selecting locked elements
    onElementSelect(elementId);
  };

  // Fix: always deselect on click away, but NOT when clicking on Transformer or its handles
  const handleStageClick = (e: any) => {
    // Check if the clicked target is part of the Transformer
    const target = e.target;
    const targetParent = target.getParent();
    
    // Don't deselect if clicking on Transformer or its handles
    if (target === transformerRef.current || 
        targetParent === transformerRef.current ||
        target.className === 'Transformer' ||
        target.className === 'TransformerHandle' ||
        target.name === 'anchor' ||
        target.name === 'rotater') {
      return;
    }
    
    // Only deselect if click is on empty part of canvas (Stage or background Rect)
    const isStage = target === target.getStage();
    const isBackgroundRect = target.className === 'Rect' && target.fill() === '#fff';
    
    if (isStage || isBackgroundRect) {
      onElementSelect(null);
    }
  };

  const handleElementDragEnd = (elementId: string, e: any) => {
    const node = e.target;
    const newElements = elements.map(el => 
      el.id === elementId 
        ? { ...el, x: node.x(), y: node.y() }
        : el
    );
    onElementsChange(newElements);
  };

  const handleElementTransformEnd = (elementId: string, e: any) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = node.rotation();

    // Reset scale and update width/height
    node.scaleX(1);
    node.scaleY(1);

    const newElements = elements.map(el => {
      if (el.id === elementId) {
        if (el.type === 'image') {
          return {
            ...el,
            x: node.x(),
            y: node.y(),
            width: Math.max(50, (el.width || 128) * scaleX),
            height: Math.max(50, (el.height || 128) * scaleY),
            rotation: rotation,
          };
        } else {
          return {
            ...el,
            x: node.x(),
            y: node.y(),
            fontSize: Math.max(8, (el.fontSize || 24) * scaleX),
            rotation: rotation,
          };
        }
      }
      return el;
    });
    onElementsChange(newElements);
    // --- Fix: update Transformer to match new node size ---
    setTimeout(() => {
      if (transformerRef.current && stageRef.current) {
        const selectedNode = stageRef.current.findOne(`#${elementId}`);
        if (selectedNode) {
          transformerRef.current.nodes([selectedNode]);
          transformerRef.current.getLayer().batchDraw();
        }
      }
    }, 0);
  };

  const handleTextChange = (elementId: string, newText: string) => {
    const newElements = elements.map(el =>
      el.id === elementId ? { ...el, text: newText } : el
    );
    onElementsChange(newElements);
  };

  // Helper: delete element by id
  const handleDeleteElement = (elementId: string) => {
    const newElements = elements.filter(el => el.id !== elementId);
    onElementsChange(newElements);
    onElementSelect(null);
  };

  // Smooth resizing: only update React state onTransformEnd for performance
  const handleElementTransform = (elementId: string, e: any) => {
    // Do nothing here; let Konva handle visual feedback
  };

  return (
    <div className="relative">
      <Stage
        ref={stageRef}
        width={width * canvasScale}
        height={height * canvasScale}
        scale={{ x: canvasScale, y: canvasScale }}
        onClick={handleStageClick}
        onTap={handleStageClick}
        onMouseDown={handleStageClick}
        style={{
          background: 'linear-gradient(45deg, #f8f9fa 25%, transparent 25%), linear-gradient(-45deg, #f8f9fa 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f8f9fa 75%), linear-gradient(-45deg, transparent 75%, #f8f9fa 75%)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
          border: '2px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Layer>
          {/* White background for export */}
          <Rect x={0} y={0} width={width} height={height} fill="#fff" />
          {/* Grid Overlay */}
          {!hideGrid && <GridPattern width={width} height={height} />}
          
          {/* Render Elements */}
          {elements.map((element) => {
            const isActive = selectedElementId === element.id;
            const elementWidth = element.type === 'image' ? (element.width || 128) : 120;
            const elementHeight = element.type === 'image' ? (element.height || 128) : (element.fontSize || 24) * 1.5;
            if (element.type === 'image' && images[element.id]) {
              return (
                <Group
                  key={element.id}
                  id={element.id}
                  x={element.x}
                  y={element.y}
                  onClick={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  onTap={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  draggable={!element.locked}
                  onDragEnd={element.locked ? undefined : (e) => handleElementDragEnd(element.id, e)}
                  onTransform={element.locked ? undefined : (e) => handleElementTransform(element.id, e)}
                  onTransformEnd={element.locked ? undefined : (e) => handleElementTransformEnd(element.id, e)}
                >
                  <Image
                    image={images[element.id]}
                    width={element.width || 128}
                    height={element.height || 128}
                    rotation={element.rotation || 0}
                  />
                  {/* Lock icon overlay if locked */}
                  {element.locked && (
                    <Group x={8} y={8}>
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="🔒"
                        fontSize={16}
                        x={-8}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#888"
                      />
                    </Group>
                  )}
                  {/* Delete icon overlay */}
                  {isActive && !element.locked && (
                    <Group
                      x={(element.width || 128) - 24}
                      y={0}
                      onClick={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      onTap={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="×"
                        fontSize={20}
                        x={-7}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#e53e3e"
                        fontStyle="bold"
                      />
                    </Group>
                  )}
                </Group>
              );
            } else if (element.type === 'shape') {
              // Render shapes based on shape type
              return (
                <Group
                  key={element.id}
                  id={element.id}
                  x={element.x}
                  y={element.y}
                  onClick={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  onTap={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  draggable={!element.locked}
                  onDragEnd={element.locked ? undefined : (e) => handleElementDragEnd(element.id, e)}
                  onTransform={element.locked ? undefined : (e) => handleElementTransform(element.id, e)}
                  onTransformEnd={element.locked ? undefined : (e) => handleElementTransformEnd(element.id, e)}
                >
                  {element.shapeType === 'rectangle' && (
                    <Rect
                      width={element.width || 120}
                      height={element.height || 80}
                      fill={element.color || '#FF6B6B'}
                      stroke={element.strokeColor || 'transparent'}
                      strokeWidth={element.strokeWidth || 0}
                      cornerRadius={4}
                      rotation={element.rotation || 0}
                    />
                  )}
                  {element.shapeType === 'circle' && (
                    <Circle
                      radius={(element.width || 100) / 2}
                      fill={element.color || '#4ECDC4'}
                      stroke={element.strokeColor || element.color || '#4ECDC4'}
                      strokeWidth={element.strokeWidth || 2}
                      rotation={element.rotation || 0}
                    />
                  )}
                  {element.shapeType === 'line' && (
                    <Line
                      points={[0, 0, element.width || 100, element.height || 0]}
                      stroke={element.color || '#3b82f6'}
                      strokeWidth={element.strokeWidth || 3}
                      lineCap="round"
                      lineJoin="round"
                      rotation={element.rotation || 0}
                    />
                  )}
                  {element.shapeType === 'arrow' && (
                    <Arrow
                      points={[0, 0, element.width || 100, element.height || 0]}
                      stroke={element.color || '#3b82f6'}
                      strokeWidth={element.strokeWidth || 3}
                      fill={element.color || '#3b82f6'}
                      pointerLength={10}
                      pointerWidth={8}
                      rotation={element.rotation || 0}
                    />
                  )}
                  {element.locked && (
                    <Group x={8} y={8}>
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="🔒"
                        fontSize={16}
                        x={-8}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#888"
                      />
                    </Group>
                  )}
                  {isActive && !element.locked && (
                    <Group
                      x={(element.width || 120) - 24}
                      y={0}
                      onClick={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      onTap={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="×"
                        fontSize={20}
                        x={-7}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#e53e3e"
                        fontStyle="bold"
                      />
                    </Group>
                  )}
                </Group>
              );
            } else if (element.type === 'sticker') {
              // Render sticky notes
              return (
                <Group
                  key={element.id}
                  id={element.id}
                  x={element.x}
                  y={element.y}
                  onClick={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  onTap={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  draggable={!element.locked}
                  onDragEnd={element.locked ? undefined : (e) => handleElementDragEnd(element.id, e)}
                  onTransform={element.locked ? undefined : (e) => handleElementTransform(element.id, e)}
                  onTransformEnd={element.locked ? undefined : (e) => handleElementTransformEnd(element.id, e)}
                >
                  <Rect
                    width={element.width || 150}
                    height={element.height || 150}
                    fill={element.stickyColor || '#fef08a'}
                    stroke="#000"
                    strokeWidth={1}
                    cornerRadius={8}
                    shadowColor="rgba(0,0,0,0.2)"
                    shadowBlur={4}
                    shadowOffset={{ x: 2, y: 2 }}
                    rotation={element.rotation || 0}
                  />
                  <Text
                    text={element.text || 'Click to edit'}
                    fontSize={element.fontSize || 16}
                    fontFamily={element.fontFamily || 'Arial'}
                    fill={element.color || '#374151'}
                    width={element.width || 150}
                    height={element.height || 150}
                    padding={10}
                    align="left"
                    verticalAlign="top"
                    rotation={element.rotation || 0}
                    onDblClick={element.locked ? undefined : () => {
                      if (onTextEdit) {
                        onTextEdit(element.id, element.text || '');
                      }
                    }}
                  />
                  {element.locked && (
                    <Group x={8} y={8}>
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="🔒"
                        fontSize={16}
                        x={-8}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#888"
                      />
                    </Group>
                  )}
                  {isActive && !element.locked && (
                    <Group
                      x={(element.width || 150) - 24}
                      y={0}
                      onClick={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      onTap={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="×"
                        fontSize={20}
                        x={-7}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#e53e3e"
                        fontStyle="bold"
                      />
                    </Group>
                  )}
                </Group>
              );
            } else if (element.type === 'drawing') {
              // Render freehand drawings
              const points = element.points?.flatMap(p => [p.x - element.x, p.y - element.y]) || [];
              return (
                <Group
                  key={element.id}
                  id={element.id}
                  x={element.x}
                  y={element.y}
                  onClick={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  onTap={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  draggable={!element.locked}
                  onDragEnd={element.locked ? undefined : (e) => handleElementDragEnd(element.id, e)}
                >
                  <Line
                    points={points}
                    stroke={element.color || '#000'}
                    strokeWidth={element.strokeWidth || 3}
                    lineCap="round"
                    lineJoin="round"
                    tension={0.5}
                    rotation={element.rotation || 0}
                  />
                  {element.locked && (
                    <Group x={8} y={8}>
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="🔒"
                        fontSize={16}
                        x={-8}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#888"
                      />
                    </Group>
                  )}
                  {isActive && !element.locked && (
                    <Group
                      x={element.width ? (element.width - 24) : 50}
                      y={0}
                      onClick={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      onTap={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="×"
                        fontSize={20}
                        x={-7}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#e53e3e"
                        fontStyle="bold"
                      />
                    </Group>
                  )}
                </Group>
              );
            } else if (element.type === 'text') {
              return (
                <Group
                  key={element.id}
                  id={element.id}
                  x={element.x}
                  y={element.y}
                  onClick={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  onTap={e => { e.cancelBubble = true; handleElementClick(element.id); }}
                  draggable={!element.locked}
                  onDragEnd={element.locked ? undefined : (e) => handleElementDragEnd(element.id, e)}
                  onTransform={element.locked ? undefined : (e) => handleElementTransform(element.id, e)}
                  onTransformEnd={element.locked ? undefined : (e) => handleElementTransformEnd(element.id, e)}
                >
                  <Text
                    text={element.text || ''}
                    fontSize={element.fontSize || 24}
                    fontFamily={element.fontFamily || 'Arial'}
                    fill={element.color || '#000000'}
                    rotation={element.rotation || 0}
                    fontStyle={`${element.bold ? 'bold' : ''} ${element.italic ? 'italic' : ''}`.trim() || 'normal'}
                    textDecoration={element.underline ? 'underline' : 'none'}
                    align={element.align || 'left'}
                    width={element.width || 200}
                    shadowBlur={0}
                    shadowColor={undefined}
                    shadowOffsetX={0}
                    shadowOffsetY={0}
                    stroke={undefined}
                    strokeWidth={0}
                    onDblClick={element.locked ? undefined : () => {
                      if (onTextEdit) {
                        onTextEdit(element.id, element.text || '');
                      }
                    }}
                  />
                  {/* Lock icon overlay if locked */}
                  {element.locked && (
                    <Group x={8} y={8}>
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="🔒"
                        fontSize={16}
                        x={-8}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#888"
                      />
                    </Group>
                  )}
                  {/* Delete icon overlay */}
                  {isActive && !element.locked && (
                    <Group
                      x={elementWidth - 24}
                      y={0}
                      onClick={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      onTap={e => { e.cancelBubble = true; handleDeleteElement(element.id); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Circle radius={12} fill="#fff" shadowBlur={2} />
                      <Text
                        text="×"
                        fontSize={20}
                        x={-7}
                        y={-10}
                        width={16}
                        height={16}
                        align="center"
                        verticalAlign="middle"
                        fill="#e53e3e"
                        fontStyle="bold"
                      />
                    </Group>
                  )}
                </Group>
              );
            }
            return null;
          })}
          
          {/* Transformer for selected element */}
          <Transformer
            ref={transformerRef}
            boundBoxFunc={(oldBox, newBox) => {
              // Limit resize
              return newBox.width < 5 ? oldBox : newBox;
            }}
          />
        </Layer>
      </Stage>
    </div>
  );
};

// Grid Pattern Component
const GridPattern: React.FC<{ width: number; height: number }> = ({ width, height }) => {
  return (
    <Group>
      {/* Vertical lines */}
      {Array.from({ length: Math.floor(width / 20) + 1 }, (_, i) => (
        <Line
          key={`v-${i}`}
          points={[i * 20, 0, i * 20, height]}
          stroke="#e5e7eb"
          strokeWidth={0.5}
          opacity={0.3}
        />
      ))}
      {/* Horizontal lines */}
      {Array.from({ length: Math.floor(height / 20) + 1 }, (_, i) => (
        <Line
          key={`h-${i}`}
          points={[0, i * 20, width, i * 20]}
          stroke="#e5e7eb"
          strokeWidth={0.5}
          opacity={0.3}
        />
      ))}
    </Group>
  );
};

export default KonvaCanvas; 